import type {
  CookieConsentGroups,
  CookieConsentValue,
  CookieGroupId,
  CookieGroupSlug
} from '../types';

export const COOKIE_CONSENT_NAME = 'selectedCookieGroup';
/** Сессионная кука: попап скрыт до закрытия браузера */
export const COOKIE_CONSENT_DISMISS_NAME = 'cookieConsentDismissed';
export const COOKIE_MAX_AGE = 60 * 60 * 24 * 30;

export const COOKIE_GROUPS = {
  necessary: { slug: 'strictlyNecessaryCookies', id: '1' },
  analytical: { slug: 'analyticalCookies', id: '2' },
  functional: { slug: 'functionalCookies', id: '3' },
  marketing: { slug: 'marketingCookies', id: '4' }
} as const satisfies Record<
  string,
  { slug: CookieGroupSlug; id: CookieGroupId }
>;

export const DEFAULT_COOKIE_GROUPS: CookieConsentGroups = {
  analytical: true,
  functional: true,
  marketing: true
};

export const REJECTED_COOKIE_GROUPS: CookieConsentGroups = {
  analytical: false,
  functional: false,
  marketing: false
};

export const buildCookieConsentValue = (
  groups: CookieConsentGroups
): CookieConsentValue => {
  const value: CookieConsentValue = {
    strictlyNecessaryCookies: COOKIE_GROUPS.necessary.id
  };

  if (groups.analytical) {
    value.analyticalCookies = COOKIE_GROUPS.analytical.id;
  }
  if (groups.functional) {
    value.functionalCookies = COOKIE_GROUPS.functional.id;
  }
  if (groups.marketing) {
    value.marketingCookies = COOKIE_GROUPS.marketing.id;
  }

  return value;
};

export const serializeCookieGroups = (groups: CookieConsentGroups) =>
  JSON.stringify(buildCookieConsentValue(groups));

export const parseCookieConsentValue = (
  value?: string | null
): CookieConsentValue | null => {
  if (!value) return null;

  try {
    const parsed = JSON.parse(value) as Partial<CookieConsentValue>;

    if (parsed.strictlyNecessaryCookies !== COOKIE_GROUPS.necessary.id) {
      return null;
    }

    return {
      strictlyNecessaryCookies: COOKIE_GROUPS.necessary.id,
      ...(parsed.analyticalCookies === COOKIE_GROUPS.analytical.id && {
        analyticalCookies: COOKIE_GROUPS.analytical.id
      }),
      ...(parsed.functionalCookies === COOKIE_GROUPS.functional.id && {
        functionalCookies: COOKIE_GROUPS.functional.id
      }),
      ...(parsed.marketingCookies === COOKIE_GROUPS.marketing.id && {
        marketingCookies: COOKIE_GROUPS.marketing.id
      })
    };
  } catch {
    return null;
  }
};

export const parseCookieGroups = (
  value?: string | null
): CookieConsentGroups | null => {
  const consent = parseCookieConsentValue(value);
  if (!consent) return null;

  return {
    analytical: hasGroup(consent, 'analyticalCookies'),
    functional: hasGroup(consent, 'functionalCookies'),
    marketing: hasGroup(consent, 'marketingCookies')
  };
};

export const hasGroup = (
  consent: CookieConsentValue,
  slug: CookieGroupSlug
): boolean => Boolean(consent[slug]);

export const toActiveGroups = (
  consent: CookieConsentValue | CookieConsentGroups
): string => {
  const value =
    'strictlyNecessaryCookies' in consent
      ? consent
      : buildCookieConsentValue(consent);

  return [
    value.strictlyNecessaryCookies,
    value.analyticalCookies,
    value.functionalCookies,
    value.marketingCookies
  ]
    .filter(Boolean)
    .join(',');
};

export type CookieConsentAccess = {
  analytical: boolean;
  functional: boolean;
  marketing: boolean;
};

export const getConsentAccess = (
  rawConsent: string | null | undefined,
  { bypass = false }: { bypass?: boolean } = {}
): CookieConsentAccess => {
  if (bypass) {
    return { analytical: true, functional: true, marketing: true };
  }

  const consent = parseCookieConsentValue(rawConsent);
  if (!consent) {
    return { analytical: true, functional: true, marketing: true };
  }

  return {
    analytical: hasGroup(consent, 'analyticalCookies'),
    functional: hasGroup(consent, 'functionalCookies'),
    marketing: hasGroup(consent, 'marketingCookies')
  };
};

/** Inline-скрипт в <head> до GTM */
export const getConsentBootstrapScript = ({
  showPopup,
  consent
}: {
  showPopup: boolean;
  consent: CookieConsentValue | null;
}) => {
  const activeGroups = consent ? toActiveGroups(consent) : null;
  const pushes: Record<string, string>[] =
    showPopup || !activeGroups
      ? [{ event: 'popupLoaded' }]
      : [
          { ActiveGroups: activeGroups },
          { event: 'CookieChecker', ActiveGroups: activeGroups }
        ];

  const body = pushes
    .map((payload) => `window.dataLayer.push(${JSON.stringify(payload)});`)
    .join('');

  return `window.dataLayer=window.dataLayer||[];${body}`;
};
